// src/services/traccar.js
// Traccar REST API integration
// Server: https://gps.pixelsouth.com
// Device port: 187.77.137.252:5015 (GT06 / Concox protocol)

import { BASE_URL, getToken } from './api';

let _config = {
  serverUrl: 'https://gps.pixelsouth.com',
  apiBase:   'https://gps.pixelsouth.com/api',
  email:     '',
  password:  '',
  token:     null,  // base64(email:password) for Basic Auth
};

export const TraccarConfig = {
  DEVICE_IP:   '187.77.137.252',
  DEVICE_PORT: 5015,

  setCredentials(serverUrl, email, password) {
    const base = serverUrl.replace(/\/$/, '');
    _config = {
      serverUrl: base,
      apiBase:   `${base}/api`,
      email,
      password,
      token: btoa(`${email}:${password}`),
    };
  },

  getServerUrl() { return _config.serverUrl; },
  getApiBase()   { return _config.apiBase; },

  // All GET/DELETE requests — Basic Auth, no Content-Type
  getHeaders() {
    if (!_config.token) return { 'Accept': 'application/json' };
    return {
      'Authorization': `Basic ${_config.token}`,
      'Accept': 'application/json',
    };
  },

  // POST/PUT requests — Basic Auth + Content-Type
  postHeaders() {
    if (!_config.token) return { 'Content-Type': 'application/json' };
    return {
      'Authorization': `Basic ${_config.token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  },

  isConfigured() { return !!_config.token; },
  getCredentials() {
    return { serverUrl: _config.serverUrl, email: _config.email, password: _config.password };
  },
};

// ─── Persist credentials ──────────────────────────────────────────────────────

export function loadSavedCredentials() {
  try {
    const saved = localStorage.getItem('traccar_creds');
    if (saved) {
      const { serverUrl, email, password } = JSON.parse(saved);
      if (email && password) {
        TraccarConfig.setCredentials(serverUrl || 'https://gps.pixelsouth.com', email, password);
        return { serverUrl, email, password };
      }
    }
  } catch {}
  return null;
}

// Bootstraps the connection from the BACKEND's shared config instead of
// requiring each browser to have been individually set up via Settings —
// this is what fixes client accounts (and any other browser/device that
// never ran the "Connect" flow) always showing "Simulation Mode" even
// though a real GPS connection is fully working for admin. Called once
// on app load for every logged-in user, regardless of role.
//
// Falls back to whatever's cached in localStorage if the backend call
// fails or the server hasn't been configured with the needed .env values
// yet — preserving the original behavior for anyone already relying on
// it, rather than breaking things during the transition.
export async function loadCredentialsFromBackend() {
  try {
    const res = await fetch(`${BASE_URL}/traccar-config`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (res.ok) {
      const data = await res.json();
      if (data.configured) {
        TraccarConfig.setCredentials(data.serverUrl, data.email, data.password);
        return true;
      }
    }
  } catch {
    // fall through to local fallback below
  }
  return !!loadSavedCredentials();
}

export function saveCredentials(serverUrl, email, password) {
  localStorage.setItem('traccar_creds', JSON.stringify({ serverUrl, email, password }));
  TraccarConfig.setCredentials(serverUrl, email, password);
}

export function clearCredentials() {
  localStorage.removeItem('traccar_creds');
  _config = {
    serverUrl: 'https://gps.pixelsouth.com',
    apiBase:   'https://gps.pixelsouth.com/api',
    email: '', password: '', token: null,
  };
}

// ─── API path candidates ──────────────────────────────────────────────────────

const API_PATH_CANDIDATES = ['/api', '/traccar/api', '/gps/api', ''];

// ─── Connection test ──────────────────────────────────────────────────────────

export async function testConnection(serverUrl, email, password) {
  const base  = (serverUrl || 'https://gps.pixelsouth.com').replace(/\/$/, '');
  const token = btoa(`${email}:${password}`);

  // Step 1 — find API base via public /server endpoint
  let apiBase = null;
  for (const path of API_PATH_CANDIDATES) {
    try {
      const res = await fetch(`${base}${path}/server`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: AbortSignal.timeout(5000),
      });
      if (res.status === 200 || res.status === 401 || res.status === 403) {
        apiBase = `${base}${path}`;
        break;
      }
    } catch {}
  }

  if (!apiBase) {
    return {
      success: false,
      error: `Cannot reach ${base} — server did not respond. Check the URL and that Traccar is running.`,
    };
  }

  // Step 2a — POST with form-urlencoded (Traccar 5.x primary method)
  try {
    const res = await fetch(`${apiBase}/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
      },
      body: `email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
      signal: AbortSignal.timeout(8000),
    });

    if (res.ok) {
      const user = await res.json();
      _config.apiBase = apiBase;
      return { success: true, user, detectedBase: apiBase, method: 'POST form-data' };
    }
    if (res.status === 401) {
      return { success: false, error: 'Wrong email or password — verify by logging into ' + base + ' directly in your browser (401)' };
    }
  } catch {}

  // Step 2b — GET with Basic Auth (fallback for older Traccar)
  try {
    const res = await fetch(`${apiBase}/session`, {
      method: 'GET',
      headers: { 'Authorization': `Basic ${token}`, 'Accept': 'application/json' },
      signal: AbortSignal.timeout(8000),
    });
    if (res.ok) {
      const user = await res.json();
      _config.apiBase = apiBase;
      return { success: true, user, detectedBase: apiBase, method: 'GET Basic Auth' };
    }
    if (res.status === 401) {
      return { success: false, error: 'Wrong email or password (401 Unauthorized)' };
    }
    const body = await res.text().catch(() => '');
    return { success: false, error: `Server returned ${res.status}${body ? ': ' + body.slice(0, 120) : ''}` };
  } catch (e) {
    if (e.name === 'AbortError' || e.name === 'TimeoutError')
      return { success: false, error: 'Connection timed out — check server URL' };
    return { success: false, error: `Network error: ${e.message}` };
  }
}

// ─── All API calls use Basic Auth directly — no cookie dependency ─────────────

/**
 * Fetch all devices — GET /api/devices
 * Uses Basic Auth so no session cookie needed
 */
export async function fetchTraccarDevices() {
  const res = await fetch(`${_config.apiBase}/devices`, {
    method: 'GET',
    headers: TraccarConfig.getHeaders(),
  });
  if (!res.ok) throw new Error(`Devices fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Fetch latest positions — GET /api/positions
 * Uses Basic Auth so no session cookie needed.
 *
 * IMPORTANT: Traccar's /api/positions endpoint binds `deviceId` as a single
 * value server-side — sending multiple repeated `deviceId=A&deviceId=B&...`
 * params (as earlier versions of this function did) is NOT reliably
 * supported and typically results in only the LAST device's position being
 * returned, silently starving every other device of updates. The safe,
 * correct approach is to fetch positions for ALL devices in one call (no
 * filter) and filter the result client-side to the devices we care about.
 */
export async function fetchPositions(deviceIds = []) {
  const res = await fetch(`${_config.apiBase}/positions`, {
    method: 'GET',
    headers: TraccarConfig.getHeaders(),
  });
  if (!res.ok) throw new Error(`Positions fetch failed: ${res.status}`);
  const all = await res.json();
  if (!deviceIds.length) return all;
  const wanted = new Set(deviceIds.map(String));
  return all.filter(p => wanted.has(String(p.deviceId)));
}

/**
 * Fetch HISTORICAL positions for one device over a date range —
 * GET /api/positions?deviceId=X&from=ISO&to=ISO
 * (Traccar's /api/positions endpoint supports this from/to range directly;
 * only when both are omitted does it fall back to "latest position only".)
 */
export async function fetchHistoricalPositions(deviceId, fromISO, toISO, { timeoutMs = 40000 } = {}) {
  const params = new URLSearchParams({ deviceId, from: fromISO, to: toISO });
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${_config.apiBase}/positions?${params}`, {
      method: 'GET',
      headers: TraccarConfig.getHeaders(),
      signal: controller.signal,
    });
    if (!res.ok) {
      const httpErr = new Error(`Historical positions fetch failed: ${res.status}`);
      httpErr.status = res.status;
      throw httpErr;
    }
    return await res.json();
  } catch (err) {
    if (err.status != null) throw err; // real HTTP error from the GPS server — already labeled above
    // Anything else means no usable response came back at all. The browser's
    // own wording for this ("NetworkError when attempting to fetch
    // resource") doesn't say what failed, so label it — this is the GPS
    // server request, not the ShipDoTrack save.
    const wrapped = new Error(
      err.name === 'AbortError' ? 'The GPS server took too long to return history'
      : err instanceof SyntaxError ? 'The GPS server sent an unreadable history response'
      : 'The GPS server didn\'t return history (connection dropped or blocked)'
    );
    wrapped.kind = err.name === 'AbortError' ? 'timeout' : 'network';
    throw wrapped;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Create a device — POST /api/devices
 */
export async function createTraccarDevice(name, uniqueId) {
  const res = await fetch(`${_config.apiBase}/devices`, {
    method: 'POST',
    headers: TraccarConfig.postHeaders(),
    body: JSON.stringify({ name, uniqueId }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Create device failed: ${res.status} — ${err}`);
  }
  return res.json();
}

/**
 * Update a device — PUT /api/devices/:id
 */
export async function updateTraccarDevice(traccarId, data) {
  const res = await fetch(`${_config.apiBase}/devices/${traccarId}`, {
    method: 'PUT',
    headers: TraccarConfig.postHeaders(),
    body: JSON.stringify({ id: traccarId, ...data }),
  });
  if (!res.ok) throw new Error(`Update device failed: ${res.status}`);
  return res.json();
}

/**
 * Delete a device — DELETE /api/devices/:id
 */
export async function deleteTraccarDevice(traccarId) {
  const res = await fetch(`${_config.apiBase}/devices/${traccarId}`, {
    method: 'DELETE',
    headers: TraccarConfig.getHeaders(),
  });
  if (!res.ok) throw new Error(`Delete device failed: ${res.status}`);
  return true;
}

/**
 * Reverse geocode using Nominatim (free, no key needed)
 */
// Haversine distance in meters — used to rank nearby POI candidates
function _geoDistMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Plain reverse geocoding only describes whatever is exactly AT a
// coordinate — if a truck idled 50m down the road from a gas station
// rather than literally inside its mapped outline, reverseGeocode() alone
// won't surface it. This does a genuine "what's nearby" search via
// Overpass (OpenStreetMap's data query API) for the closest named place
// within `radiusMeters`, so idle stops read as "Near Shell Gas Station,
// Highway 1" instead of just the bare road name.
async function findNearestEstablishment(lat, lng, radiusMeters = 150) {
  try {
    const query = `[out:json][timeout:8];(node["name"](around:${radiusMeters},${lat},${lng});way["name"](around:${radiusMeters},${lat},${lng}););out center 15;`;

    // Overpass's public server can be slow or occasionally unresponsive —
    // don't let a hung request block the rest of the app indefinitely.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const res = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      // fetch() defaults to a "text/plain" Content-Type for a plain string
      // body, which Overpass's form-decoder doesn't reliably parse — this
      // must be explicit for the `data=` parameter to be read correctly.
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(query),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!res.ok) return null;
    const data = await res.json();
    const candidates = (data.elements || [])
      .map(el => {
        const elLat = el.lat ?? el.center?.lat;
        const elLng = el.lon ?? el.center?.lon;
        if (elLat == null || elLng == null || !el.tags?.name) return null;
        // Prefer actual establishments (amenity/shop/tourism/etc) over
        // incidental named things like a stretch of sidewalk.
        const isEstablishment = !!(el.tags.amenity || el.tags.shop || el.tags.tourism ||
          el.tags.leisure || el.tags.office || el.tags.craft || el.tags.healthcare);
        return {
          name: el.tags.name,
          distance: _geoDistMeters(lat, lng, elLat, elLng),
          isEstablishment,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.isEstablishment === b.isEstablishment ? a.distance - b.distance : a.isEstablishment ? -1 : 1));

    return candidates[0] || null;
  } catch {
    // Network error, timeout, CORS issue, rate limit, or malformed
    // response — any of these just means no nearby-establishment name is
    // available this time. The caller already falls back to plain
    // road/locality text, so this fails silently and safely.
    return null;
  }
}

// Nominatim's usage policy caps free requests at roughly 1/second. Live GPS
// position polling can otherwise fire several of these back-to-back (once
// per truck that moved) with no natural spacing, which is exactly what was
// triggering repeated 429 (rate limited) responses — and once rate-limited,
// Nominatim's error response often omits CORS headers too, so the browser
// reports a CORS failure on top of the underlying 429. Queue every request
// through here so they're always spaced out, regardless of how many fire
// at once.
let _nominatimQueue = Promise.resolve();
function _throttledNominatimFetch(url) {
  const run = () => fetch(url, { headers: { 'Accept-Language': 'en' } }).finally(() => new Promise(r => setTimeout(r, 1100)));
  const result = _nominatimQueue.then(run, run);
  _nominatimQueue = result.catch(() => {});
  return result;
}

// Short-lived cache so re-requesting the same (or nearly the same) spot
// within a couple of minutes — very common for a truck idling or moving
// slowly — returns instantly instead of going back to the network and
// waiting in the throttle queue again.
const _reverseGeocodeCache = new Map(); // "lat,lng" (5dp) -> { value, expiresAt }
const REVERSE_GEOCODE_CACHE_MS = 2 * 60 * 1000;
function _cacheKey(lat, lng) {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`; // ~11m precision — close enough to count as "the same spot"
}

export async function reverseGeocode(lat, lng, options = {}) {
  // options.skipNearbySearch: for high-frequency callers (live GPS position
  // polling can fire this every ~10 seconds per moving truck, with no
  // rate-limiting of its own) — the Overpass "what's nearby" lookup adds a
  // second network call each time and was the actual source of repeated
  // "POST overpass-api.de/api/interpreter" failures, since nothing was
  // throttling how often it got hit. Live tracking doesn't need the
  // extra precision that lookup adds; skip it there and keep it only for
  // lower-frequency, deliberate calls (manual map-click placement, and the
  // Sync Travel History import, which already rate-limits itself).
  // options._retried is used internally for the one-time network retry.
  const { skipNearbySearch = false, _retried = false } = typeof options === 'boolean' ? { _retried: options } : options;

  const cacheKey = _cacheKey(lat, lng);
  const cached = _reverseGeocodeCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  let data;
  try {
    const res = await _throttledNominatimFetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&namedetails=1&zoom=18`
    );
    if (!res.ok) throw new Error(`status ${res.status}`);
    data = await res.json();
  } catch (err) {
    // One retry on a transient network/rate-limit hiccup before giving up —
    // this is what was previously causing an otherwise-geocodable point to
    // fall straight to raw coordinates for no real reason.
    if (!_retried) {
      await new Promise(r => setTimeout(r, 800));
      return reverseGeocode(lat, lng, { skipNearbySearch, _retried: true });
    }
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }

  const addr = data.address || {};
  const { road, suburb, city, town, municipality, province, state, country } = addr;
  const locality = suburb || city || town || municipality;

  // Nominatim tags a named establishment (gas station, terminal, mall,
  // warehouse, restaurant, school, hospital, landmark, etc.) under one of
  // these address keys, or in namedetails/the top-level `name`, but only
  // when the point is essentially ON that place. Prefer that first.
  const establishment =
    data.namedetails?.name ||
    data.name ||
    addr.amenity || addr.shop || addr.tourism || addr.leisure ||
    addr.office || addr.craft || addr.healthcare || addr.government ||
    addr.historic || addr.man_made ||
    (addr.building && addr.building !== 'yes' ? addr.building : null);

  let result;
  if (establishment) {
    const rest = [road, locality].filter(part => part && part !== establishment);
    result = rest.length ? `${establishment}, ${rest.join(', ')}` : establishment;
  } else {
    // Nothing named exactly at this point — check what's nearby instead,
    // unless the caller opted out (see note above).
    const nearby = skipNearbySearch ? null : await findNearestEstablishment(lat, lng);
    if (nearby && nearby.name !== road) {
      const rest = [road, locality].filter(part => part && part !== nearby.name);
      const prefix = nearby.distance < 30 ? 'At' : 'Near';
      result = rest.length ? `${prefix} ${nearby.name}, ${rest.join(', ')}` : `${prefix} ${nearby.name}`;
    } else {
      // No landmark anywhere nearby — always prefer whatever real
      // place-name text Nominatim gave us (road, then locality, then
      // province, then country) over raw coordinates. Only fall back to
      // numbers if literally none of that exists, which is extremely rare.
      const parts = [road, locality, province || state, country].filter(Boolean);
      if (parts.length) {
        result = parts.slice(0, 3).join(', ');
      } else {
        const fallback = data.display_name?.split(',').map(s => s.trim()).filter(Boolean).slice(0, 3).join(', ');
        result = fallback || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      }
    }
  }

  _reverseGeocodeCache.set(cacheKey, { value: result, expiresAt: Date.now() + REVERSE_GEOCODE_CACHE_MS });
  return result;
}

/**
 * Parse Traccar position → app location object
 * Speed from Traccar is in knots → convert to km/h
 */
export function parsePosition(pos) {
  return {
    lat:        pos.latitude,
    lng:        pos.longitude,
    speed:      Math.round((pos.speed || 0) * 1.852),
    heading:    courseToHeading(pos.course || 0),
    altitude:   pos.altitude,
    accuracy:   pos.accuracy,
    fixTime:    pos.fixTime,
    serverTime: pos.serverTime,
    attributes: pos.attributes || {},
    // Traccar flags a position `valid: false` when the device didn't get
    // a real, trustworthy fix — this is what was letting some GPS units
    // show a location clear across the world (often China, for devices
    // manufactured/factory-tested there) instead of their real position
    // in the Philippines. Cheap trackers sometimes report a cached last-
    // known position (occasionally still from factory testing) when they
    // can't get a fresh fix, rather than reporting nothing at all —
    // Traccar correctly flags that as invalid, but nothing downstream was
    // checking this flag before now.
    valid: pos.valid !== false,
  };
}

function courseToHeading(course) {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(course / 45) % 8];
}

export function getStatusFromPosition(pos, device) {
  if (!pos) return 'offline';
  if (device?.status === 'offline') return 'offline';
  return (pos.speed || 0) * 1.852 > 3 ? 'moving' : 'idle';
}
